/**
 * DataTable and DetailPage — the spine of the console.
 *
 * Every queue in this product is the same loop: table → filter → focused row → detail → confirm.
 * Getting this pair right is most of the console; the 42 screens are largely compositions of it.
 *
 * Density is the point. `compact` is 36px rows, `comfortable` 44px — an ops desk scans hundreds of
 * rows and every wasted pixel is a row they cannot see. Money and ids render monospace and
 * right-aligned so columns line up under each other for comparison.
 */
import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { Button, Icon } from './primitives';
import { Input } from './forms';
import type { IconName } from './icons';
import { Money, Section } from './kit';
import type { components } from '../api/generated/schema';
import { formatDateTime } from '../lib/datetime';

type BookingLedgerLine = components['schemas']['BookingLedgerLine'];

/* ────────────────────────────  DataTable  ──────────────────────────── */

export type Density = 'compact' | 'comfortable';

export interface Column {
  key: string;
  label: string;
  align?: 'left' | 'right';
  /** Ids, hashes and amounts. Tabular numerals so digits line up vertically. */
  mono?: boolean;
  /** Marks the column the server sorted by — display only; sorting is the API's job. */
  sort?: 'asc' | 'desc';
}

export interface Row {
  /** String from a fixture, integer from the API — the table only uses it as a React key. */
  id: string | number;
  [key: string]: ReactNode;
}

export interface DataTableProps {
  columns: readonly Column[];
  rows: readonly Row[];
  density?: Density;
  footer?: ReactNode;
  /** Index of the keyboard-focused row — the row Enter acts on. */
  focusedIndex?: number;
  onFocusRow?: (index: number) => void;
  onRowClick?: (row: Row) => void;
  /** Ids currently ticked, for batch actions like a payout run. */
  selected?: readonly (string | number)[];
  emptyLabel?: string;
  style?: CSSProperties;
}

/** Sort direction glyphs, hoisted out of JSX so no condition literal sits inside `name={…}`. */
const SORT_ICON = { asc: 'arrow-up', desc: 'chevron-down' } satisfies Record<'asc' | 'desc', IconName>;

const ROW_HEIGHT: Record<Density, string> = {
  compact: 'var(--row-compact)',
  comfortable: 'var(--row-comfortable)',
};

export function DataTable({
  columns,
  rows,
  density = 'compact',
  footer,
  focusedIndex,
  onFocusRow,
  onRowClick,
  selected,
  emptyLabel = 'Nothing to show',
  style,
}: DataTableProps) {
  return (
    <div
      style={{
        border: '1px solid var(--c-border)',
        borderRadius: 'var(--r-tile)',
        background: 'var(--c-surface)',
        overflow: 'hidden',
        ...style,
      }}
    >
      {/* Focusable so a keyboard can scroll a table wider than its column (WCAG 2.1.1). */}
      <div tabIndex={0} style={{ overflowX: 'auto' }}>
        {/* `minWidth: max-content` is what makes the `overflowX` above do anything. Without it a
            `width: 100%` table compresses its columns to fit and the last one is clipped by the
            rounded corner instead of scrolling — which is how a status column ends up reading
            "Partially s…" in a narrow two-column layout. */}
        <table
          style={{
            width: '100%',
            minWidth: 'max-content',
            borderCollapse: 'collapse',
            fontSize: 'var(--fs-admin-cell)',
          }}
        >
          <thead>
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  // aria-sort tells assistive tech what the server ordered by.
                  aria-sort={c.sort ? (c.sort === 'asc' ? 'ascending' : 'descending') : undefined}
                  style={{
                    textAlign: c.align === 'right' ? 'right' : 'left',
                    padding: '6px var(--sp-md)',
                    background: 'var(--c-sunk)',
                    color: 'var(--c-ink3)',
                    fontFamily: 'var(--font-display)',
                    fontSize: 'var(--fs-admin-colhead)',
                    fontWeight: 'var(--fw-regular)',
                    textTransform: 'uppercase',
                    letterSpacing: 'var(--tracking-kicker)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {c.label}
                  {c.sort && <Icon name={SORT_ICON[c.sort]} size={11} style={{ marginLeft: 4 }} />}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  style={{ padding: 'var(--sp-xl)', textAlign: 'center', color: 'var(--c-ink3)' }}
                >
                  {emptyLabel}
                </td>
              </tr>
            ) : (
              rows.map((row, i) => {
                const focused = focusedIndex === i;
                const ticked = selected?.includes(row.id) ?? false;
                return (
                  <tr
                    key={row.id}
                    aria-selected={ticked || undefined}
                    onMouseEnter={() => onFocusRow?.(i)}
                    onClick={() => onRowClick?.(row)}
                    // A clickable row is reachable without a pointer: Tab to it, ↑/↓ between
                    // rows, Enter opens it (doc 03 §8).
                    tabIndex={onRowClick ? 0 : undefined}
                    onKeyDown={
                      onRowClick
                        ? (event) => {
                            const tr = event.currentTarget;
                            if (event.key === 'Enter') onRowClick(row);
                            else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                              event.preventDefault();
                              const next = event.key === 'ArrowDown' ? tr.nextElementSibling : tr.previousElementSibling;
                              (next as HTMLElement | null)?.focus();
                            }
                          }
                        : undefined
                    }
                    style={{
                      height: ROW_HEIGHT[density],
                      cursor: onRowClick ? 'pointer' : undefined,
                      // Focus is a saffron edge AND a tinted row — never colour alone.
                      background: focused ? 'var(--c-saffron-50)' : ticked ? 'var(--c-sunk)' : 'transparent',
                      boxShadow: focused ? 'inset 3px 0 0 var(--c-saffron)' : undefined,
                    }}
                  >
                    {columns.map((c) => (
                      <td
                        key={c.key}
                        className={c.mono ? 'mono-num' : undefined}
                        style={{
                          padding: '0 var(--sp-md)',
                          borderTop: '1px solid var(--c-border)',
                          textAlign: c.align === 'right' ? 'right' : 'left',
                          color: 'var(--c-ink2)',
                          verticalAlign: 'middle',
                        }}
                      >
                        {row[c.key]}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {footer && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--sp-md)',
            justifyContent: 'space-between',
            padding: 'var(--sp-sm) var(--sp-md)',
            borderTop: '1px solid var(--c-border)',
            background: 'var(--c-sunk)',
            fontSize: 'var(--fs-admin-colhead)',
            color: 'var(--c-ink3)',
          }}
        >
          {footer}
        </div>
      )}
    </div>
  );
}

/* ────────────────────────────  DetailPage  ──────────────────────────── */

export interface DetailPageProps {
  open?: boolean;
  title: string;
  /** The public code — mono, because operators read it aloud to people on the phone. */
  code?: string;
  status?: ReactNode;
  tabs?: readonly string[];
  activeTab?: string;
  onTab?: (tab: string) => void;
  actions?: ReactNode;
  children?: ReactNode;
  /** Every entity view carries its audit trail. A consequential action lands here visibly. */
  audit?: ReactNode;
  onClose?: () => void;
  style?: CSSProperties;
}

/**
 * The detail view takes over the content area rather than sliding a narrow drawer over it —
 * the money and KYC screens need the width, and a drawer that covers the table it came from
 * makes comparing the two impossible.
 */
export function DetailPage({
  open = true,
  title,
  code,
  status,
  tabs,
  activeTab,
  onTab,
  actions,
  children,
  audit,
  onClose,
  style,
}: DetailPageProps) {
  useEffect(() => {
    if (!open || !onClose) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <section
      aria-label={title}
      style={{
        display: 'grid',
        gridTemplateRows: 'auto auto minmax(0, 1fr) auto',
        background: 'var(--c-bg)',
        border: '1px solid var(--c-border)',
        borderRadius: 'var(--r-card)',
        overflow: 'hidden',
        ...style,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-md)',
          padding: 'var(--sp-lg)',
          background: 'var(--c-surface)',
          borderBottom: '1px solid var(--c-border)',
        }}
      >
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              height: 32,
              padding: '0 var(--sp-sm) 0 6px',
              border: '1px solid var(--c-border-strong)',
              background: 'var(--c-surface2)',
              borderRadius: 'var(--r-tile)',
              color: 'var(--c-ink2)',
              font: 'inherit',
              fontSize: 'var(--fs-admin-cell)',
              cursor: 'pointer',
            }}
          >
            <Icon name="arrow-left" size={16} />
            Back
          </button>
        )}
        <div style={{ display: 'grid', gap: 2, minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-sm)', flexWrap: 'wrap' }}>
            <h2
              style={{
                margin: 0,
                fontFamily: 'var(--font-display)',
                fontSize: 'var(--fs-admin-page-title)',
                fontWeight: 'var(--fw-semibold)',
                color: 'var(--c-ink)',
              }}
            >
              {title}
            </h2>
            {status}
          </div>
          {code && (
            <span className="mono-num" style={{ fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink3)' }}>
              {code}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 'var(--sp-sm)', alignItems: 'center' }}>{actions}</div>
      </div>

      {tabs && tabs.length > 0 && (
        <div
          role="tablist"
          style={{
            display: 'flex',
            gap: 'var(--sp-lg)',
            padding: '0 var(--sp-lg)',
            background: 'var(--c-surface)',
            borderBottom: '1px solid var(--c-border)',
          }}
        >
          {tabs.map((t) => {
            const on = t === activeTab;
            return (
              <button
                key={t}
                type="button"
                role="tab"
                aria-selected={on}
                onClick={() => onTab?.(t)}
                style={{
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  padding: '10px 0',
                  font: 'inherit',
                  fontFamily: 'var(--font-display)',
                  fontSize: 'var(--fs-admin-ui)',
                  fontWeight: on ? 'var(--fw-semibold)' : 'var(--fw-regular)',
                  color: on ? 'var(--c-ink)' : 'var(--c-ink3)',
                  borderBottom: `2px solid ${on ? 'var(--c-saffron)' : 'transparent'}`,
                }}
              >
                {t}
              </button>
            );
          })}
        </div>
      )}

      <div style={{ overflowY: 'auto', padding: 'var(--sp-xxl) var(--sp-lg)' }}>
        <div style={{ display: 'grid', gap: 'var(--sp-lg)', alignContent: 'start', maxWidth: 1120 }}>{children}</div>
      </div>

      {audit && (
        <div
          style={{
            padding: 'var(--sp-md) var(--sp-lg)',
            background: 'var(--c-sunk)',
            borderTop: '1px solid var(--c-border)',
            display: 'grid',
            gap: 2,
          }}
        >
          <span className="kicker">Audit trail</span>
          <span style={{ fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink2)' }}>{audit}</span>
        </div>
      )}
    </section>
  );
}

export interface OrderedRow {
  primary: string;
  secondary: string;
}

export interface OrderedListEditorProps {
  rows: readonly OrderedRow[];
  addLabel: string;
  disabled?: boolean;
  onSave: (rows: OrderedRow[]) => void;
  /** Editing help under each field as it is typed — the content lint's in-place marks. */
  hint?: (text: string) => ReactNode;
}

/**
 * An editor for a list whose **order is its meaning** — a puja's steps, its FAQs.
 *
 * These are backed by replace-the-whole-list endpoints: the array sent becomes the list, and array
 * position becomes `sort_order`. So reordering is done by reordering, and there is one Save rather
 * than a save per row — a per-row save against a whole-list replace would send the other rows too
 * and silently persist edits the operator had not finished.
 *
 * **Local until saved, and it says so.** A draft that autosaved would replace the live list on
 * every keystroke. The dirty marker is what tells an operator the page they are looking at is not
 * yet the page a devotee sees.
 */
export function OrderedListEditor({ rows, addLabel, disabled = false, onSave, hint }: OrderedListEditorProps) {
  const [draft, setDraft] = useState<OrderedRow[]>([...rows]);
  const [baseline, setBaseline] = useState<readonly OrderedRow[]>(rows);

  /* Re-seed when a different puja is opened. Keeping one puja's steps under another's name is the
     failure this guards. */
  if (baseline !== rows) {
    setBaseline(rows);
    setDraft([...rows]);
  }

  const dirty = JSON.stringify(draft) !== JSON.stringify(rows);

  /* No bounds guard: `Up` is disabled at the first row and `Down` at the last, so the boundary is
     visible in the markup rather than duplicated as an unreachable early return. */
  const move = (index: number, by: number) => {
    const next = [...draft];
    [next[index], next[index + by]] = [next[index + by]!, next[index]!];
    setDraft(next);
  };

  const update = (index: number, patch: Partial<OrderedRow>) =>
    setDraft(draft.map((r, i) => (i === index ? { ...r, ...patch } : r)));

  return (
    <div style={{ display: 'grid', gap: 'var(--sp-sm)', marginTop: 'var(--sp-md)' }}>
      {draft.length === 0 && (
        <p style={{ color: 'var(--c-ink3)', fontSize: 'var(--fs-admin-colhead)' }}>
          Nothing listed yet.
        </p>
      )}
      {draft.map((row, i) => (
        <div
          key={i}
          style={{
            display: 'grid',
            gap: 'var(--sp-xs)',
            padding: 'var(--sp-sm) var(--sp-md)',
            border: '1px solid var(--c-border)',
            borderRadius: 'var(--r-tile)',
            background: 'var(--c-surface2)',
          }}
        >
          <div style={{ display: 'flex', gap: 'var(--sp-sm)', alignItems: 'center' }}>
            <span style={{ color: 'var(--c-ink3)', fontSize: 'var(--fs-admin-colhead)' }}>{i + 1}</span>
            <Input
              label={`${addLabel} ${i + 1}`}
              value={row.primary}
              onChange={(v) => update(i, { primary: v })}
              density="admin"
              disabled={disabled}
              style={{ flex: 1 }}
            />
            <Button size="sm" variant="ghost" disabled={disabled || i === 0} onClick={() => move(i, -1)}>
              Up
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={disabled || i === draft.length - 1}
              onClick={() => move(i, 1)}
            >
              Down
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={disabled}
              onClick={() => setDraft(draft.filter((_, j) => j !== i))}
            >
              Remove
            </Button>
          </div>
          {hint?.(row.primary)}
          <Input
            label="Detail"
            value={row.secondary}
            onChange={(v) => update(i, { secondary: v })}
            density="admin"
            multiline
            disabled={disabled}
          />
          {hint?.(row.secondary)}
        </div>
      ))}
      <div style={{ display: 'flex', gap: 'var(--sp-sm)', alignItems: 'center' }}>
        <Button
          size="sm"
          variant="secondary"
          disabled={disabled}
          onClick={() => setDraft([...draft, { primary: '', secondary: '' }])}
        >
          Add {addLabel.toLowerCase()}
        </Button>
        <Button size="sm" disabled={disabled || !dirty} onClick={() => onSave(draft)}>
          Save {addLabel.toLowerCase()}s
        </Button>
        {dirty && (
          <span style={{ color: 'var(--c-warn-text)', fontSize: 'var(--fs-admin-colhead)' }}>
            Unsaved — a devotee still sees the old list.
          </span>
        )}
      </div>
    </div>
  );
}

/* ──────────────────────  The booking's money  ────────────────────── */

/**
 * One booking's ledger lines, with the running effect per account.
 *
 * ⛔ **Presentational on purpose — it takes lines, it does not fetch them.** Nothing else in this
 * kit reaches for a hook, and that boundary is worth keeping: a component that fetched would make
 * every test of it a test of the query client too. Each screen owns its own loading and error
 * states, which is right, because "could not read the booking's money" means something different
 * inside a journal than it does inside a payment.
 *
 * It is shared rather than written twice because the same question — what did this booking's money
 * actually do — is asked from the ledger and from the payments drawer, and two renderings of one
 * answer drift. The running balance in particular is the server's arithmetic; showing it two
 * different ways would eventually show it two different values.
 */
export function BookingLedgerLines({
  lines,
  title = "This booking's money",
}: {
  lines: readonly BookingLedgerLine[];
  title?: string;
}) {
  if (lines.length === 0) {
    return (
      <Section title={title} note="nothing has moved yet">
        <p style={{ margin: 0, fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink2)' }}>
          No ledger entry references this booking. Money accrues when it is captured, and the
          provider payable accrues at completion — so an accepted, unpaid booking has none.
        </p>
      </Section>
    );
  }
  return (
    <Section title={title} note="running effect per account, computed by the server" pad={false}>
      <DataTable
        density="compact"
        columns={[
          { key: 'when', label: 'Occurred', mono: true },
          { key: 'event', label: 'Event' },
          { key: 'account', label: 'Account' },
          { key: 'amount', label: 'Amount', align: 'right' },
          { key: 'running', label: 'Running', align: 'right' },
        ]}
        rows={lines.map((line, index) => ({
          id: `${line.transaction_id}-${index}`,
          when: formatDateTime(line.occurred_at),
          event: line.txn_type,
          account: `${line.account_code} ${line.account_name}`,
          amount: <Money paise={line.amount_paise} />,
          running: <Money paise={line.running_paise} bold />,
        }))}
      />
    </Section>
  );
}

/* ────────────────────────────  Pager  ──────────────────────────── */

export interface PagerProps {
  page: number;
  pageSize: number;
  total: number;
  onPage: (page: number) => void;
}

/**
 * Previous / Next under a paged list, as the refunds and ledger tables draw it. It renders nothing
 * while everything fits on one page, so a short list carries no dead controls.
 */
export function Pager({ page, pageSize, total, onPage }: PagerProps) {
  const pages = total > pageSize ? Math.ceil(total / pageSize) : 1;
  /* `[` and `]` page the list from anywhere on the screen (doc 03 §8), except while typing. */
  useEffect(() => {
    if (pages <= 1) return;
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      if (e.key === '[' && page > 1) onPage(page - 1);
      else if (e.key === ']' && page < pages) onPage(page + 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [page, pages, onPage]);
  // Written as "not more than a page" so a missing count draws nothing rather than "of NaN".
  if (!(total > pageSize)) return null;
  return (
    <div style={{ display: 'flex', gap: 'var(--sp-sm)', justifyContent: 'flex-end', alignItems: 'center' }}>
      <span className="mono-num">
        Page {page} of {pages} · {total} in all
      </span>
      <button type="button" disabled={page <= 1} onClick={() => onPage(page - 1)}>
        Previous
      </button>
      <button type="button" disabled={page >= pages} onClick={() => onPage(page + 1)}>
        Next
      </button>
    </div>
  );
}
